import QRCode from 'qrcode';
import { supabase } from '../supabase';

// Certificado oficial do INEP (modelo em api/_modelos/certificado-inep-modelo.html, entregue
// só para quem está logado por /api/certificado-modelo). O navegador troca as [[variáveis]],
// gera o QR code da página de validação e abre a impressão (A4 paisagem → "Salvar como PDF").
// Regras do LEIA-ME do pacote: escapar o texto; curso_modulos já vai em HTML; nenhuma
// variável pode sobrar sem valor.

const SITE_VALIDACAO = 'gestaoquality.com.br/validar/';
const ALFABETO = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // sem 0/O e 1/I

export function gerarCodigoOficial() {
  const n = new Uint32Array(8);
  crypto.getRandomValues(n);
  const s = [...n].map(x => ALFABETO[x % ALFABETO.length]).join('');
  return `${s.slice(0, 4)}-${s.slice(4)}`;
}

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const paraData = v => { if (!v) return null; const d = new Date(v?.toDate ? v.toDate() : v); return isNaN(d) ? null : d; };
export const dataBR = v => { const d = paraData(v); return d ? d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : ''; };
export const dataExtenso = v => {
  const d = paraData(v); if (!d) return '';
  const [dia, mes, ano] = d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }).split('/');
  return `${Number(dia)} de ${MESES[Number(mes) - 1]} de ${ano}`;
};
export const soDigitos = s => String(s || '').replace(/\D/g, '');
export const formatarCpf = s => { const d = soDigitos(s); return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : ''; };
export function cpfValido(s) {
  const d = soDigitos(s);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const dv = n => { let soma = 0; for (let i = 0; i < n; i++) soma += Number(d[i]) * (n + 1 - i); const r = (soma * 10) % 11; return r === 10 ? 0 : r; };
  return dv(9) === Number(d[9]) && dv(10) === Number(d[10]);
}
// "maria DA silva" → "Maria da Silva"
export function nomeProprio(s) {
  const minusculas = new Set(['da', 'de', 'do', 'das', 'dos', 'e']);
  return String(s || '').trim().replace(/\s+/g, ' ').toLowerCase().split(' ')
    .map((p, i) => (i > 0 && minusculas.has(p) ? p : p.charAt(0).toUpperCase() + p.slice(1))).join(' ');
}
// Nota 0–100 do INEP → "9,2" (escala do certificado)
export const notaBR = n => (typeof n === 'number' ? (n / 10).toFixed(1).replace('.', ',') : 'Não se aplica');

const escapar = s => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

async function buscarModelo() {
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch('/api/certificado-modelo', { headers: { Authorization: `Bearer ${session?.access_token}` } });
  if (!r.ok) throw new Error(r.status === 401 ? 'Sessão expirada — entre de novo.' : `Não foi possível carregar o modelo (${r.status}).`);
  return r.text();
}

// dados: objeto com as variáveis do LEIA-ME (curso_modulos como lista de textos)
export async function montarHtml(dados) {
  const d = { ...dados };
  const url = `https://${d.certificado_validacao_url}`;
  d.qrcode_img = await QRCode.toDataURL(url, { errorCorrectionLevel: 'M', margin: 1, width: 300, color: { dark: '#1F1F1F', light: '#FFFFFF' } });
  const modulos = Array.isArray(d.curso_modulos) ? d.curso_modulos : [];
  const htmlModulos = `<ul>${(modulos.length ? modulos : ['Conteúdo do curso']).map(m => `<li>${escapar(m)}</li>`).join('')}</ul>`;
  let html = (await buscarModelo()).replace(/\[\[([a-z0-9_]+)\]\]/g, (todo, nome) => {
    if (nome === 'curso_modulos') return htmlModulos;
    if (!(nome in d) || d[nome] === '' || d[nome] == null) return todo;
    return escapar(d[nome]);
  });
  const faltando = [...new Set([...html.matchAll(/\[\[([a-z0-9_]+)\]\]/g)].map(m => m[1]))];
  if (faltando.length) throw new Error('Faltam dados para o certificado: ' + faltando.join(', '));
  // Nome do arquivo ao salvar em PDF = título da página
  const titulo = escapar(`Certificado INEP - ${d.curso_titulo} - ${d.aluno_nome}`);
  html = /<title>[\s\S]*?<\/title>/i.test(html) ? html.replace(/<title>[\s\S]*?<\/title>/i, `<title>${titulo}</title>`) : html.replace(/<head>/i, `<head><title>${titulo}</title>`);
  return html;
}

// Abre o certificado numa janela e chama a impressão. A janela precisa ser aberta
// no clique (antes de qualquer espera), senão o navegador bloqueia como pop-up.
export function abrirJanelaCertificado() {
  const w = window.open('', '_blank');
  if (w) w.document.write('<p style="font:16px sans-serif;padding:40px;color:#334">Gerando o certificado…</p>');
  return w;
}
export async function imprimirNaJanela(w, html) {
  if (!w) throw new Error('O navegador bloqueou a janela do certificado. Libere pop-ups para gestaoquality.com.br e tente de novo.');
  w.document.open(); w.document.write(html); w.document.close();
  await new Promise(r => { if (w.document.readyState === 'complete') r(); else w.addEventListener('load', r, { once: true }); });
  try { await w.document.fonts.ready; } catch (_) { /* segue */ }
  await new Promise(r => setTimeout(r, 400)); // o modelo ajusta o tamanho das letras ao carregar
  w.focus();
  w.print();
}

export const urlValidacao = cod => `${SITE_VALIDACAO}${cod}`;
